import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Badge,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Collapse,
  Fab,
  Zoom,
  useTheme,
  Pagination,
  Stack,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person,
  School,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Wc,
  CheckCircle,
  Pending,
  Cancel,
  HourglassEmpty,
  Visibility,
  Edit,
  Delete,
  Download,
  Search,
  GridView,
  ViewList,
  People,
  Male,
  Female,
  ExpandMore,
  ExpandLess,
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  Save,
  Close,
  Warning,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../services/api';

const schoolColors = {
  primary: '#1a237e',
  secondary: '#0d47a1',
  accent: '#ff6f00',
  gold: '#ffd700',
  dark: '#0a0a2a',
  light: '#e3f2fd',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#ffffff',
};

const StyledCard = styled(Card)(({ theme, status }) => ({
  background: status === 'approved'
    ? `linear-gradient(135deg, ${schoolColors.gold}, ${schoolColors.accent})`
    : status === 'pending'
    ? `linear-gradient(135deg, ${schoolColors.accent}, #ffb74d)`
    : status === 'rejected'
    ? 'linear-gradient(135deg, #f44336 0%, #e57373 100%)'
    : `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
  color: 'white',
  borderRadius: 20,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-10px) scale(1.02)',
    boxShadow: `0 20px 40px ${schoolColors.primary}40`,
  },
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: `1px solid ${schoolColors.primary}20`,
  boxShadow: `0 8px 32px ${schoolColors.primary}15`,
}));

const SchoolButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 600,
  background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
  color: schoolColors.white,
  '&:hover': {
    background: `linear-gradient(135deg, ${schoolColors.secondary}, ${schoolColors.primary})`,
    transform: 'translateY(-2px)',
  },
}));

const Students = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterForm, setFilterForm] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedStudent, setExpandedStudent] = useState(null);

  // ✅ New state for delete and update
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    byForm: {},
    byGender: { male: 0, female: 0 },
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/students');
      const data = Array.isArray(response.data) ? response.data : [];
      setStudents(data);
      calculateStats(data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setSnackbar({ open: true, message: 'Failed to fetch student data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const computed = {
      total: data.length,
      approved: data.filter(s => s.status === 'approved').length,
      byForm: {},
      byGender: { male: 0, female: 0 },
    };
    data.forEach(student => {
      const form = student.applying_for_level;
      computed.byForm[form] = (computed.byForm[form] || 0) + 1;
      if (student.gender === 'ME') computed.byGender.male++;
      else if (student.gender === 'KE') computed.byGender.female++;
    });
    setStats(computed);
  };

  // ✅ Handle Edit — open dialog with student data
  const handleEditOpen = (student) => {
    setEditData({
      id: student.id,
      first_name: student.first_name || '',
      middle_name: student.middle_name || '',
      last_name: student.last_name || '',
      gender: student.gender || '',
      applying_for_level: student.applying_for_level || '',
      status: student.status || '',
      ward: student.ward || '',
      district: student.district || '',
      region: student.region || '',
      primary_school: student.primary_school || '',
      exam_number: student.exam_number || '',
      completion_year: student.completion_year || '',
      religion: student.religion || '',
      citizenship: student.citizenship || '',
    });
    setEditOpen(true);
  };

  // ✅ Handle Save Update
  const handleSaveUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/students/${editData.id}`, editData);
      setSnackbar({ open: true, message: 'Student updated successfully!', severity: 'success' });
      setEditOpen(false);
      fetchStudents();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to update student', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ✅ Handle Delete — open confirmation
  const handleDeleteOpen = (student) => {
    setStudentToDelete(student);
    setDeleteConfirmOpen(true);
  };

  // ✅ Handle Confirm Delete
  const handleConfirmDelete = async () => {
    if (!studentToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/students/${studentToDelete.id}`);
      setSnackbar({ open: true, message: `${studentToDelete.first_name} ${studentToDelete.last_name} deleted successfully`, severity: 'success' });
      setDeleteConfirmOpen(false);
      setStudentToDelete(null);
      setDetailsOpen(false);
      fetchStudents();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to delete student', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const filteredAndSortedStudents = useMemo(() => {
    let result = [...students];
    result = result.filter(student => {
      const matchesSearch =
        student.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.primary_school?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesForm = filterForm === 'all' || student.applying_for_level === filterForm;
      const matchesStatus = filterStatus === 'all' || student.status === filterStatus;
      const matchesGender = filterGender === 'all' ||
        (filterGender === 'male' && student.gender === 'ME') ||
        (filterGender === 'female' && student.gender === 'KE');
      return matchesSearch && matchesForm && matchesStatus && matchesGender;
    });
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name': comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`); break;
        case 'form': comparison = a.applying_for_level.localeCompare(b.applying_for_level); break;
        case 'date': comparison = new Date(a.submitted_date) - new Date(b.submitted_date); break;
        case 'registration': comparison = a.registration_number.localeCompare(b.registration_number); break;
        default: comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [students, searchTerm, filterForm, filterStatus, filterGender, sortBy, sortOrder]);

  const paginatedStudents = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedStudents.slice(start, start + rowsPerPage);
  }, [filteredAndSortedStudents, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedStudents.length / rowsPerPage);

  const handleViewDetails = useCallback((student) => {
    setSelectedStudent(student);
    setDetailsOpen(true);
  }, []);

  const handleExpandStudent = useCallback((studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  }, [expandedStudent]);

  const handleExportCSV = () => {
    const csvData = filteredAndSortedStudents.map(s => ({
      'Registration': s.registration_number,
      'Name': `${s.first_name} ${s.middle_name || ''} ${s.last_name}`,
      'Gender': s.gender === 'ME' ? 'Male' : 'Female',
      'Form': s.applying_for_level?.replace('form', 'Form '),
      'Status': s.status,
      'Date of Birth': s.date_of_birth ? new Date(s.date_of_birth).toLocaleDateString() : '',
      'Primary School': s.primary_school,
      'Ward': s.ward,
      'District': s.district,
      'Region': s.region,
      'Submission Date': new Date(s.submitted_date).toLocaleDateString(),
    }));
    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `kaozya_students_${new Date().toISOString().split('T')[0]}.xlsx`);
    setSnackbar({ open: true, message: 'Student list exported!', severity: 'success' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'rejected': return <Cancel />;
      case 'waiting_list': return <HourglassEmpty />;
      default: return <Person />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[1,2,3,4,5,6].map(i => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* Page Banner */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 3, textAlign: 'center', background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`, boxShadow: `0 10px 30px ${schoolColors.primary}40` }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: schoolColors.white, mb: 0.5 }}>Students Management</Typography>
        <Typography variant="body1" sx={{ color: schoolColors.gold }}>View, edit and manage all students</Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Students', value: stats.total, icon: <People /> },
          { label: 'Male', value: stats.byGender.male, icon: <Male /> },
          { label: 'Female', value: stats.byGender.female, icon: <Female /> },
          { label: 'Active', value: stats.approved, icon: <School /> },
        ].map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: index * 0.1 }}>
              <GlassCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: `${schoolColors.primary}20`, color: schoolColors.primary, width: 56, height: 56 }}>
                      {item.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: schoolColors.primary }}>{item.value}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </GlassCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, registration or school..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: schoolColors.primary }} /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Form</InputLabel>
                <Select value={filterForm} onChange={(e) => setFilterForm(e.target.value)} label="Form">
                  <MenuItem value="all">All Forms</MenuItem>
                  <MenuItem value="form1">Form 1</MenuItem>
                  <MenuItem value="form2">Form 2</MenuItem>
                  <MenuItem value="form3">Form 3</MenuItem>
                  <MenuItem value="form5">Form 5</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="waiting_list">Waiting List</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select value={filterGender} onChange={(e) => setFilterGender(e.target.value)} label="Gender">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth variant="outlined" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  startIcon={sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
                  sx={{ borderColor: schoolColors.primary, color: schoolColors.primary }}>
                  {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                </Button>
                <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} sx={{ color: schoolColors.primary }}>
                  {viewMode === 'grid' ? <ViewList /> : <GridView />}
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Results count */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedStudents.length} of {filteredAndSortedStudents.length} students
        </Typography>
        <SchoolButton startIcon={<Download />} onClick={handleExportCSV} size="small">
          Export Excel
        </SchoolButton>
      </Box>

      {/* Grid View */}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          <AnimatePresence>
            {paginatedStudents.map((student, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                >
                  <StyledCard status={student.status}>
                    <CardContent onClick={() => handleExpandStudent(student.id)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Avatar sx={{ width: 70, height: 70, bgcolor: 'white', color: schoolColors.primary, fontSize: 28, fontWeight: 700 }}>
                          {student.first_name?.[0]}{student.last_name?.[0]}
                        </Avatar>
                        <Chip size="small" label={student.status} sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} />
                      </Box>

                      <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                        {student.first_name} {student.last_name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85, fontFamily: 'monospace', display: 'block' }}>
                        {student.registration_number}
                      </Typography>

                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" label={student.applying_for_level?.replace('form', 'Form ')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                        <Chip size="small" icon={student.gender === 'ME' ? <Male sx={{ color: 'white !important' }} /> : <Female sx={{ color: 'white !important' }} />}
                          label={student.gender === 'ME' ? 'Male' : 'Female'}
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                      </Box>

                      <Collapse in={expandedStudent === student.id} sx={{ mt: 1.5 }}>
                        <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                          <LocationOn sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                          {student.ward}, {student.district}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.5 }}>
                          <School sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                          {student.primary_school}
                        </Typography>
                      </Collapse>
                    </CardContent>

                    {/* ✅ Action buttons */}
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, pt: 0 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(student); }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Student">
                          <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                            onClick={(e) => { e.stopPropagation(); handleEditOpen(student); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Student">
                          <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,50,50,0.3)', '&:hover': { bgcolor: 'rgba(255,50,50,0.5)' } }}
                            onClick={(e) => { e.stopPropagation(); handleDeleteOpen(student); }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <IconButton size="small" sx={{ color: 'white' }} onClick={() => handleExpandStudent(student.id)}>
                        {expandedStudent === student.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </CardActions>
                  </StyledCard>
                </motion.div>
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      ) : (
        /* List View */
        <GlassCard>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {['Student', 'Registration', 'Form', 'Gender', 'Status', 'Location', 'Actions'].map(col => (
                    <TableCell key={col} sx={{ bgcolor: schoolColors.primary, color: schoolColors.white, fontWeight: 700 }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedStudents.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: schoolColors.primary, color: schoolColors.white }}>
                          {student.first_name?.[0]}{student.last_name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: schoolColors.primary }}>
                            {student.first_name} {student.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{student.primary_school}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{student.registration_number}</TableCell>
                    <TableCell>
                      <Chip size="small" label={student.applying_for_level?.replace('form', 'Form ')}
                        sx={{ bgcolor: `${schoolColors.primary}15`, color: schoolColors.primary }} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                        icon={student.gender === 'ME' ? <Male /> : <Female />}
                        label={student.gender === 'ME' ? 'Male' : 'Female'}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={student.status}
                        color={student.status === 'approved' ? 'success' : student.status === 'pending' ? 'warning' : 'error'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{student.ward}</Typography>
                      <Typography variant="caption" color="text.secondary">{student.district}, {student.region}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View">
                          <IconButton size="small" sx={{ color: schoolColors.primary }} onClick={() => handleViewDetails(student)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" sx={{ color: schoolColors.accent }} onClick={() => handleEditOpen(student)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" sx={{ color: '#f44336' }} onClick={() => handleDeleteOpen(student)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassCard>
      )}

      {/* Pagination */}
      {filteredAndSortedStudents.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={totalPages} page={page + 1}
            onChange={(e, newPage) => { setPage(newPage - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            color="primary" size="large" showFirstButton showLastButton />
        </Box>
      )}

      {/* ✅ Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth TransitionComponent={Zoom}>
        <DialogTitle sx={{ bgcolor: schoolColors.primary, color: schoolColors.white }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Edit />
              <Typography variant="h6">Edit Student — {editData.first_name} {editData.last_name}</Typography>
            </Box>
            <IconButton onClick={() => setEditOpen(false)} sx={{ color: 'white' }}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="First Name" value={editData.first_name || ''}
                onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Middle Name" value={editData.middle_name || ''}
                onChange={(e) => setEditData({ ...editData, middle_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Last Name" value={editData.last_name || ''}
                onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select value={editData.gender || ''} onChange={(e) => setEditData({ ...editData, gender: e.target.value })} label="Gender">
                  <MenuItem value="ME">Male</MenuItem>
                  <MenuItem value="KE">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Form Level</InputLabel>
                <Select value={editData.applying_for_level || ''} onChange={(e) => setEditData({ ...editData, applying_for_level: e.target.value })} label="Form Level">
                  <MenuItem value="form1">Form 1</MenuItem>
                  <MenuItem value="form2">Form 2</MenuItem>
                  <MenuItem value="form3">Form 3</MenuItem>
                  <MenuItem value="form5">Form 5</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={editData.status || ''} onChange={(e) => setEditData({ ...editData, status: e.target.value })} label="Status">
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="waiting_list">Waiting List</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Religion" value={editData.religion || ''}
                onChange={(e) => setEditData({ ...editData, religion: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Ward" value={editData.ward || ''}
                onChange={(e) => setEditData({ ...editData, ward: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="District" value={editData.district || ''}
                onChange={(e) => setEditData({ ...editData, district: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Region" value={editData.region || ''}
                onChange={(e) => setEditData({ ...editData, region: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Primary School" value={editData.primary_school || ''}
                onChange={(e) => setEditData({ ...editData, primary_school: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Exam Number" value={editData.exam_number || ''}
                onChange={(e) => setEditData({ ...editData, exam_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Completion Year" type="number" value={editData.completion_year || ''}
                onChange={(e) => setEditData({ ...editData, completion_year: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined">Cancel</Button>
          <SchoolButton onClick={handleSaveUpdate} disabled={saving} startIcon={<Save />}>
            {saving ? 'Saving...' : 'Save Changes'}
          </SchoolButton>
        </DialogActions>
      </Dialog>

      {/* ✅ Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="h6">Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All related data including marks and guardian information will be permanently deleted.
          </Alert>
          {studentToDelete && (
            <Box sx={{ p: 2, bgcolor: '#fff3f3', borderRadius: 2, border: '1px solid #ffcdd2' }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {studentToDelete.first_name} {studentToDelete.middle_name} {studentToDelete.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">{studentToDelete.registration_number}</Typography>
              <Typography variant="body2" color="text.secondary">{studentToDelete.applying_for_level?.replace('form', 'Form ')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error"
            disabled={deleting} startIcon={<Delete />}>
            {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth TransitionComponent={Zoom}>
        {selectedStudent && (
          <>
            <DialogTitle sx={{ background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: 'white', color: schoolColors.primary }}>
                    {selectedStudent.first_name?.[0]}{selectedStudent.last_name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedStudent.first_name} {selectedStudent.last_name}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>{selectedStudent.registration_number}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Edit">
                    <IconButton sx={{ color: 'white' }} onClick={() => { setDetailsOpen(false); handleEditOpen(selectedStudent); }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton sx={{ color: '#ffcdd2' }} onClick={() => { setDetailsOpen(false); handleDeleteOpen(selectedStudent); }}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 2 }}>
                <Tab label="Personal Info" />
                <Tab label="Education" />
              </Tabs>
              {selectedTab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Full Name</Typography><Typography>{selectedStudent.first_name} {selectedStudent.middle_name} {selectedStudent.last_name}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography><Typography>{selectedStudent.date_of_birth ? new Date(selectedStudent.date_of_birth).toLocaleDateString() : 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Gender</Typography><Typography>{selectedStudent.gender === 'ME' ? 'Male' : 'Female'}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Religion</Typography><Typography>{selectedStudent.religion}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip size="small" label={selectedStudent.status}
                      color={selectedStudent.status === 'approved' ? 'success' : selectedStudent.status === 'pending' ? 'warning' : 'error'} />
                  </Grid>
                  <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Address</Typography><Typography>{selectedStudent.ward} Ward, {selectedStudent.district} District, {selectedStudent.region} Region</Typography></Grid>
                </Grid>
              )}
              {selectedTab === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Applying For</Typography><Typography>{selectedStudent.applying_for_level?.replace('form', 'Form ')}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Primary School</Typography><Typography>{selectedStudent.primary_school}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Exam Number</Typography><Typography>{selectedStudent.exam_number}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Completion Year</Typography><Typography>{selectedStudent.completion_year}</Typography></Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>

      {/* FAB scroll to top */}
      <Zoom in={filteredAndSortedStudents.length > 0}>
        <Fab sx={{ position: 'fixed', bottom: 20, right: 20, bgcolor: schoolColors.primary, '&:hover': { bgcolor: schoolColors.accent } }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <ArrowUpward sx={{ color: schoolColors.white }} />
        </Fab>
      </Zoom>
    </Container>
  );
};

export default Students;
